import { Skeleton } from '@askable/ui/core/skeleton';
import { cn } from '@askable/ui/lib/utils';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'src/lib/routing';
import { useAIQueryAccess } from 'src/hooks/useAIQueryAccess';
import { useProductAccess } from 'src/hooks/useProductAccess';
import { useQuery } from 'urql';

import { ChatHistory as ChatHistoryQuery } from 'containers/AIQuery/containers/AIQueryLandingContainer/data/ChatHistory.query';
import { isImpersonating } from 'utils/impersonation';

type ChatHistoryProps = {
  isCollapsed: boolean;
};

export const CHAT_HISTORY_LIMIT = {
  industry_stream: 10,
  default: 5,
} as const;

export const ChatHistory = memo(({ isCollapsed }: ChatHistoryProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;

  // When impersonating as a client via the Admin
  // ChatHistory returns an auth error so don't load it
  const connectedAsClient = isImpersonating();
  const queryAccess = useAIQueryAccess();
  const shouldShowChatHistory = queryAccess && !connectedAsClient;
  const { activeProduct } = useProductAccess();

  const [{ data, fetching }] = useQuery({
    query: ChatHistoryQuery,
    variables: {
      first: CHAT_HISTORY_LIMIT[activeProduct ?? 'default'],
    },
    requestPolicy: 'cache-and-network',
    pause: !shouldShowChatHistory,
  });

  // @todo remove this filter when schema is updated
  const chatHistoryList = data?.chatHistory?.nodes || [];

  if (!shouldShowChatHistory) {
    return null;
  }

  return (
    <section
      className={cn(
        'flex w-64 flex-1 flex-col gap-2 overflow-hidden px-3 py-2 opacity-100 transition-all duration-150 starting:opacity-0',
        {
          'pointer-events-none opacity-0': isCollapsed,
        },
      )}
    >
      {fetching && !data ? (
        <ul className="flex flex-col gap-0.5">
          <Skeleton className="h-7 w-full" />
          <Skeleton className="h-7 w-full" />
          <Skeleton className="h-7 w-2/3" />
        </ul>
      ) : null}

      {!fetching && chatHistoryList.length > 0 ? (
        <>
          <h4 className="px-2 text-xs text-muted-foreground">{t('sections.navigation.recentChats')}</h4>

          <ul className="flex flex-1 flex-col gap-0.5 overflow-auto">
            {chatHistoryList.map(chat => {
              const isActive = pathname === `/chat/${chat.id}`;
              return (
                <li key={chat.id}>
                  <Link
                    to={`/chat/${chat.id}`}
                    className={cn(
                      'flex h-7 items-center rounded-sm px-2 text-[13px] font-medium text-nav-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground focus:bg-foreground/[0.04] focus:text-foreground dark:hover:bg-foreground/[0.065] dark:focus:bg-foreground/[0.065]',
                      {
                        'bg-foreground/[0.065] text-foreground hover:bg-foreground/[0.065] dark:bg-foreground/[0.11] dark:hover:bg-foreground/[0.11]':
                          isActive,
                      },
                    )}
                  >
                    <div className="truncate">{chat.title}</div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </section>
  );
});

ChatHistory.displayName = 'ChatHistory';
